import React, { useState, useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Card, Row, Col, Tabs, Tab, Form } from 'react-bootstrap';
import 'highcharts/modules/drilldown';

// Función para determinar la cuadrilla según tus reglas
const getCuadrilla = (emp) => {
    if (emp.cargo === 'OBRERO DE PATIO') return 'OBREROS DE PATIO';
    if (emp.cargo === 'SUPERVISOR') return 'SUPERVISORES';
    if (emp.horario === '00:00-00:00' && emp.horas === 24) return '24 HORAS';
    if (emp.horario === '00:00-00:00' && emp.horas === 0) return 'DESCANSO';
    if (emp.horario === '06:00-14:00') return 'CUADRILLA 1';
    if (emp.horario === '14:00-22:00') return 'CUADRILLA 2';
    if (emp.horario === '22:00-06:00') return 'CUADRILLA 3';
    return 'OTROS';
};

export const DOReportAnalytics = ({ reporteActividades, dataEmpleadosHoy, mobile,  }) => {
    // Filtros sección empleados
    const [filtroCuadrilla, setFiltroCuadrilla] = useState('');
    const [filtroCargo, setFiltroCargo] = useState('');
    const [filtroEmpleado, setFiltroEmpleado] = useState('');

    // Filtros sección operaciones
    const [filtroOperacion, setFiltroOperacion] = useState('');
    const [filtroActividad, setFiltroActividad] = useState('');

    // --- Estados para KPIs y charts ---
    const [totalEmpleados, setTotalEmpleados] = useState(0);
    const [empleadoMasHoras, setEmpleadoMasHoras] = useState('');
    const [promedioHoras, setPromedioHoras] = useState(0);
    const [chartHorasPorEmpleado, setChartHorasPorEmpleado] = useState({});
    const [chartEmpleadosPorCargo, setChartEmpleadosPorCargo] = useState({});
    const [chartHorasPorCargoDrill, setChartHorasPorCargoDrill] = useState({});

    const [totalOperaciones, setTotalOperaciones] = useState(0);
    const [operacionMasFrecuente, setOperacionMasFrecuente] = useState('');
    const [promedioActividades, setPromedioActividades] = useState(0);
    const [chartActividadesPorOperacion, setChartActividadesPorOperacion] = useState({});
    const [chartHorasPorOperacion, setChartHorasPorOperacion] = useState({});
    const [chartActividadesPorHora, setChartActividadesPorHora] = useState({});

    // --- Mapea empleados con cuadrilla calculada ---
    const empleadosConCuadrilla = dataEmpleadosHoy.map(emp => ({
        ...emp,
        cuadrilla: getCuadrilla(emp)
    }));

    // --- Filtrado de datos según filtros seleccionados ---
    const empleadosFiltrados = empleadosConCuadrilla.filter(emp =>
        (filtroCuadrilla ? emp.cuadrilla === filtroCuadrilla : true) &&
        (filtroCargo ? emp.cargo === filtroCargo : true) &&
        (filtroEmpleado ? emp.empleado === filtroEmpleado : true)
    );

    const operacionesFiltradas = reporteActividades.filter(act =>
        (filtroOperacion ? act.operacion === filtroOperacion : true) &&
        (filtroActividad ? act.actividad === filtroActividad : true)
    );

    // --- Opciones para los selects ---
    const cuadrillas = [...new Set(empleadosConCuadrilla.map(emp => emp.cuadrilla).filter(Boolean))];
    const cargos = [...new Set(empleadosConCuadrilla.map(emp => emp.cargo).filter(Boolean))];
    const empleados = [...new Set(empleadosConCuadrilla.map(emp => emp.empleado).filter(Boolean))];

    const operaciones = [...new Set(reporteActividades.map(act => act.operacion).filter(Boolean))];
    const actividades = [...new Set(reporteActividades.map(act => act.actividad).filter(Boolean))];

    // --- Opciones para gráficos según mobile ---
    const chartResponsive =
    {
        chart: { spacing: [5, 5, 5, 5], height: 220 },
        title: { style: { fontSize: '13px' } },
        xAxis: {
            labels: { style: { fontSize: '11px' } },
            title: { style: { fontSize: '12px' } }
        },
        yAxis: {
            labels: { style: { fontSize: '11px' } },
            title: { style: { fontSize: '12px' } }
        },
        legend: { itemStyle: { fontSize: '11px' } },
        plotOptions: {
            series: {
                dataLabels: { style: { fontSize: '11px' } }
            }
        },
        credits: { enabled: false },
        accessibility: {
            enabled: false
        }
    }

    // Utilidad para evitar pasar undefined a Highcharts
    const safeAxis = (axis, fallbackTitle) => ({
        ...(axis?.categories ? { categories: axis.categories } : {}),
        ...(axis?.type ? { type: axis.type } : {}),
        ...(axis?.labels ? { labels: axis.labels } : {}),
        title: { text: fallbackTitle, ...(axis?.title || {}) }
    });

    useEffect(() => {
        // KPIs empleados
        setTotalEmpleados(empleadosFiltrados.length);

        const empleadosOrdenados = [...empleadosFiltrados]
            .sort((a, b) => (b.horas || 0) - (a.horas || 0));
        setEmpleadoMasHoras(empleadosOrdenados[0]?.empleado || '');

        const totalHoras = empleadosFiltrados
            .reduce((acc, emp) => acc + (emp.horas || 0), 0);
        setPromedioHoras(empleadosFiltrados.length ? (totalHoras / empleadosFiltrados.length).toFixed(2) : 0);

        // Horas por empleado (mayor a menor)
        setChartHorasPorEmpleado({
            chart: { type: 'bar', ...(chartResponsive.chart || {}) },
            title: { text: 'Horas x Empleado' },
            xAxis: safeAxis({
                categories: empleadosOrdenados.map(emp => emp.empleado),
                labels: chartResponsive.xAxis?.labels,
                title: {
                    enabled: false
                }
            }, 'Empleado'),
            yAxis: safeAxis({
                labels: chartResponsive.yAxis?.labels,
                title: {
                    enabled: false
                }
            }, 'Horas'),
            plotOptions: {
                series: {
                    dataLabels: { enabled: !mobile, ...(chartResponsive.plotOptions?.series?.dataLabels || {}) }
                }
            },
            legend: {
                enabled: false
            },
            credits: { enabled: false },
            series: [{
                name: 'Horas',
                data: empleadosOrdenados.map(emp => emp.horas || 0)
            }],
            accessibility: {
                enabled: false
            }
        });

        // Empleados por cargo (pie)
        const empleadosPorCargo = {};
        empleadosFiltrados.forEach(emp => {
            if (!empleadosPorCargo[emp.cargo]) empleadosPorCargo[emp.cargo] = 0;
            empleadosPorCargo[emp.cargo]++;
        });

        setChartEmpleadosPorCargo({
            chart: { type: 'pie', ...(chartResponsive.chart || {}) },
            title: { text: 'Empleados x Cargo', },
            tooltip: { pointFormat: '{series.name}: <b>{point.y}</b>' },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: !mobile,
                        format: '<b>{point.name}</b>: {point.y}',
                        ...(chartResponsive.plotOptions?.series?.dataLabels || {})
                    }
                }
            },
            legend: chartResponsive.legend,
            credits: { enabled: false },
            series: [{
                name: 'Cantidad',
                data: Object.entries(empleadosPorCargo).map(([cargo, cantidad]) => ({ name: cargo, y: cantidad }))
            }],
            accessibility: {
                enabled: false
            }
        });

        // Drilldown 3 niveles: Cuadrilla > Cargo > Empleado
        const cuadrillasObj = {};
        empleadosFiltrados.forEach(emp => {
            if (!cuadrillasObj[emp.cuadrilla]) cuadrillasObj[emp.cuadrilla] = [];
            cuadrillasObj[emp.cuadrilla].push(emp);
        });

        const seriesData = [];
        const drilldownSeries = [];

        Object.entries(cuadrillasObj).forEach(([cuadrilla, empleadosCuadrilla]) => {
            // Agrupa por cargo dentro de la cuadrilla
            const cargosObj = {};
            empleadosCuadrilla.forEach(emp => {
                if (!cargosObj[emp.cargo]) cargosObj[emp.cargo] = [];
                cargosObj[emp.cargo].push(emp);
            });

            // Suma total horas por cuadrilla
            const totalHorasCuadrilla = empleadosCuadrilla.reduce((acc, emp) => acc + (emp.horas || 0), 0);
            seriesData.push({
                name: cuadrilla,
                y: totalHorasCuadrilla,
                drilldown: cuadrilla
            });

            // Segundo nivel: cargos dentro de cuadrilla
            const cargosData = [];
            Object.entries(cargosObj).forEach(([cargo, empleadosCargo]) => {
                const totalHorasCargo = empleadosCargo.reduce((acc, emp) => acc + (emp.horas || 0), 0);
                cargosData.push({
                    name: cargo,
                    y: totalHorasCargo,
                    drilldown: `${cuadrilla}|${cargo}`
                });

                // Tercer nivel: empleados dentro de cargo y cuadrilla
                drilldownSeries.push({
                    id: `${cuadrilla}|${cargo}`,
                    name: `Empleados (${cargo} - ${cuadrilla})`,
                    data: empleadosCargo
                        .sort((a, b) => (b.horas || 0) - (a.horas || 0))
                        .map(emp => [emp.empleado, emp.horas || 0])
                });
            });

            drilldownSeries.push({
                id: cuadrilla,
                name: `Cargos (${cuadrilla})`,
                data: cargosData
            });
        });

        setChartHorasPorCargoDrill({
            chart: {
                type: 'column',
                ...(chartResponsive.chart || {})
            },
            title: {
                text: 'Horas x Cuadrilla x Cargo x Empleado',
            },
            xAxis: safeAxis({
                type: 'category',
                labels: chartResponsive.xAxis?.labels,
                title: {
                    enabled: false
                }
            }, 'Cuadrilla'),
            yAxis: safeAxis({
                labels: chartResponsive.yAxis?.labels,
                title: {
                    enabled: false
                }
            }, 'Horas'),
            legend: {
                enabled: false,
            },
            plotOptions: {
                series: {
                    borderWidth: 0,
                    dataLabels: {
                        enabled: !mobile,
                        ...(chartResponsive.plotOptions?.series?.dataLabels || {})
                    }
                }
            },
            credits: { enabled: false },
            series: [{
                name: 'Cuadrillas',
                colorByPoint: true,
                data: seriesData.sort((a, b) => b.y - a.y)
            }],
            drilldown: {
                series: drilldownSeries
            },
            accessibility: {
                enabled: false
            }
        });

        // KPIs operaciones
        setTotalOperaciones([...new Set(operacionesFiltradas.map(act => act.operacion))].length);

        // Operación más frecuente
        const operacionesCount = {};
        operacionesFiltradas.forEach(act => {
            if (!operacionesCount[act.operacion]) operacionesCount[act.operacion] = 0;
            operacionesCount[act.operacion]++;
        });
        const operacionesOrdenadas = Object.entries(operacionesCount).sort((a, b) => b[1] - a[1]);
        setOperacionMasFrecuente(operacionesOrdenadas[0]?.[0] || '');

        // Promedio de actividades por operación
        setPromedioActividades(operacionesOrdenadas.length ? (operacionesFiltradas.length / operacionesOrdenadas.length).toFixed(2) : 0);

        // Actividades por operación (mayor a menor)
        setChartActividadesPorOperacion({
            chart: { type: 'column', ...(chartResponsive.chart || {}) },
            title: { text: 'Actividades x Operación' },
            xAxis: safeAxis({
                categories: operacionesOrdenadas.map(([op]) => op),
                labels: chartResponsive.xAxis?.labels,
                title: {
                    enabled: false
                },
            }, 'Operación'),
            yAxis: safeAxis({
                labels: chartResponsive.yAxis?.labels,
                title: {
                    enabled: false
                }
            }, 'Cantidad'),
            plotOptions: {
                series: {
                    dataLabels: { enabled: !mobile, format: '{point.y}', ...(chartResponsive.plotOptions?.series?.dataLabels || {}) }
                }
            },
            legend: {
                enabled: false
            },
            credits: { enabled: false },
            series: [{
                name: 'Actividades',
                data: operacionesOrdenadas.map(([_, count]) => count)
            }],
            accessibility: {
                enabled: false
            }
        });

        // Drilldown Horas por Operación > Actividad
        const horasPorOperacion = {};
        const actividadesPorOperacion = {};
        operacionesFiltradas.forEach(act => {
            if (!horasPorOperacion[act.operacion]) horasPorOperacion[act.operacion] = 0;
            horasPorOperacion[act.operacion] += act.total || 0;

            if (!actividadesPorOperacion[act.operacion]) actividadesPorOperacion[act.operacion] = {};
            if (!actividadesPorOperacion[act.operacion][act.actividad]) actividadesPorOperacion[act.operacion][act.actividad] = 0;
            actividadesPorOperacion[act.operacion][act.actividad] += act.total || 0;
        });

        const operacionesHorasOrdenadas = Object.entries(horasPorOperacion).sort((a, b) => b[1] - a[1]);
        const seriesDataOp = [];
        const drilldownSeriesOp = [];

        operacionesHorasOrdenadas.forEach(([operacion, horas]) => {
            seriesDataOp.push({
                name: operacion,
                y: horas,
                drilldown: operacion
            });

            // Drilldown: actividades de esa operación
            const actividadesData = Object.entries(actividadesPorOperacion[operacion] || {})
                .sort((a, b) => b[1] - a[1])
                .map(([actividad, horas]) => [actividad, horas]);
            drilldownSeriesOp.push({
                id: operacion,
                name: `Actividades (${operacion})`,
                data: actividadesData
            });
        });

        setChartHorasPorOperacion({
            chart: { type: 'bar', ...(chartResponsive.chart || {}) },
            title: { text: 'Horas x Operación x Actividad' },
            xAxis: safeAxis({
                type: 'category',
                labels: chartResponsive.xAxis?.labels,
                title: {
                    enabled: false
                },
            }, 'Operación'),
            yAxis: safeAxis({
                labels: chartResponsive.yAxis?.labels,
                title: {
                    enabled: false
                },
            }, 'Horas'),
            legend: {
                enabled: false
            },
            plotOptions: {
                series: {
                    borderWidth: 0,
                    dataLabels: {
                        enabled: !mobile, format: '{point.y}',
                        ...(chartResponsive.plotOptions?.series?.dataLabels || {})
                    }
                }
            },
            credits: { enabled: false },
            series: [{
                name: 'Operaciones',
                colorByPoint: true,
                data: seriesDataOp
            }],
            drilldown: { series: drilldownSeriesOp },
            accessibility: {
                enabled: false
            }
        });

        // Actividades por hora del día
        const actividadesPorHora = {};
        operacionesFiltradas.forEach(act => {
            const hora = act.desde ? act.desde.split(':')[0] : 'Sin hora';
            if (!actividadesPorHora[hora]) actividadesPorHora[hora] = 0;
            actividadesPorHora[hora]++;
        });
        const horasOrdenadas = Object.entries(actividadesPorHora).sort((a, b) => a[0] - b[0]);
        setChartActividadesPorHora({
            chart: { type: 'line', ...(chartResponsive.chart || {}) },
            title: { text: 'Actividades x Hora del Día', },
            xAxis: safeAxis({
                categories: horasOrdenadas.map(([hora]) => hora),
                labels: chartResponsive.xAxis?.labels,
                title: {
                    text: 'Hora del Día',
                },
            }, 'Hora'),
            yAxis: safeAxis({
                labels: chartResponsive.yAxis?.labels,
                title: {
                    text: 'Cantidad de Actividades',
                },
            }, 'Actividades'),
            plotOptions: {
                series: {
                    dataLabels: { enabled: !mobile, format: '{point.y}', ...(chartResponsive.plotOptions?.series?.dataLabels || {}) }
                }
            },
            legend: {
                enabled: false
            },
            credits: { enabled: false },
            series: [{
                name: 'Actividades',
                data: horasOrdenadas.map(([_, count]) => count)
            }],
            accessibility: {
                enabled: false
            }
        });

        // eslint-disable-next-line
    }, [
        dataEmpleadosHoy, reporteActividades,
        filtroCuadrilla, filtroCargo, filtroEmpleado,
        filtroOperacion, filtroActividad, mobile
    ]);

    // --- Clases y estilos responsive ---
    const colSize = mobile ? 12 : undefined;
    const cardClass = mobile ? 'mb-2' : '';
    const rowClass = mobile ? 'mb-1' : 'mb-3';

    return (
        <div style={mobile ? { padding: 4 } : {}}>
            <Tabs
                defaultActiveKey="empleados"
                className={`mb-2 nav-pills justify-content-center ${mobile ? 'small' : ''}`}
                variant="pills"
                tabclassname="w-auto"
            >
                <Tab eventKey="empleados"
                    title={<span style={{ fontWeight: 'bold', fontSize: mobile ? 13 : undefined }}>Por Empleados</span>}>
                    <Row className={rowClass}>
                        <Col md={3} xs={12}>
                            <Form.Select
                                size="sm"
                                value={filtroCuadrilla}
                                onChange={e => setFiltroCuadrilla(e.target.value)}>
                                <option value="">Todas las cuadrillas</option>
                                {cuadrillas.map(c => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={3} xs={12} className={mobile ? 'mt-2' : ''}>
                            <Form.Select
                                size="sm"
                                value={filtroCargo}
                                onChange={e => setFiltroCargo(e.target.value)}>
                                <option value="">Todos los cargos</option>
                                {cargos.map(c => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={3} xs={12} className={mobile ? 'mt-2' : ''}>
                            <Form.Select
                                size="sm"
                                value={filtroEmpleado}
                                onChange={e => setFiltroEmpleado(e.target.value)}>
                                <option value="">Todos los empleados</option>
                                {empleados.map(e => <option key={e} value={e}>{e}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={3} xs={12} className={mobile ? 'mt-2' : ''}>
                            <button
                                className={`btn btn-sm w-100 ${filtroCuadrilla || filtroCargo || filtroEmpleado
                                    ? 'btn-warning'
                                    : 'btn-outline-secondary'}`}
                                style={mobile ? { fontSize: 13, padding: '6px 0' } : {}}
                                onClick={() => {
                                    setFiltroCuadrilla('');
                                    setFiltroCargo('');
                                    setFiltroEmpleado('');
                                }}>
                                Limpiar filtros
                            </button>
                        </Col>
                    </Row>
                    <Row className={rowClass}>
                        <Col md={4} xs={colSize} className={cardClass}>
                            <Card body className="text-center" style={mobile ? { fontSize: 13, padding: 8 } : {}}>
                                <h6 style={mobile ? { fontSize: 12 } : {}}>Empleados activos</h6>
                                <h3 style={mobile ? { fontSize: 18 } : {}}>{totalEmpleados}</h3>
                            </Card>
                        </Col>
                        <Col md={4} xs={colSize} className={cardClass}>
                            <Card body className="text-center" style={mobile ? { fontSize: 13, padding: 8 } : {}}>
                                <h6 style={mobile ? { fontSize: 12 } : {}}>Empleado con más horas</h6>
                                <h3 style={mobile ? { fontSize: 18 } : {}}>{empleadoMasHoras}</h3>
                            </Card>
                        </Col>
                        <Col md={4} xs={colSize} className={cardClass}>
                            <Card body className="text-center" style={mobile ? { fontSize: 13, padding: 8 } : {}}>
                                <h6 style={mobile ? { fontSize: 12 } : {}}>Promedio horas/empleado</h6>
                                <h3 style={mobile ? { fontSize: 18 } : {}}>{promedioHoras}</h3>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} xs={colSize}>
                            {chartHorasPorCargoDrill.series &&
                                <div className="chart-shadow" style={mobile ? { padding: 4 } : {}}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartHorasPorCargoDrill} />
                                </div>}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} xs={colSize} className={mobile ? 'mb-2' : ''}>
                            {chartHorasPorEmpleado.series &&
                                <div className="chart-shadow" style={mobile ? { padding: 4 } : {}}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartHorasPorEmpleado} />
                                </div>}
                        </Col>
                        <Col md={6} xs={colSize}>
                            {chartEmpleadosPorCargo.series &&
                                <div className="chart-shadow" style={mobile ? { padding: 4 } : {}}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartEmpleadosPorCargo} />
                                </div>}
                        </Col>
                    </Row>
                </Tab>

                <Tab eventKey="operaciones"
                    title={<span style={{ fontWeight: 'bold', fontSize: mobile ? 13 : undefined }}>Por Operaciones</span>}>
                    <Row className={rowClass}>
                        <Col md={4} xs={12}>
                            <Form.Select
                                size="sm"
                                value={filtroOperacion}
                                onChange={e => setFiltroOperacion(e.target.value)}>
                                <option value="">Todas las operaciones</option>
                                {operaciones.map(o => <option key={o} value={o}>{o}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={4} xs={12} className={mobile ? 'mt-2' : ''}>
                            <Form.Select
                                size="sm"
                                value={filtroActividad}
                                onChange={e => setFiltroActividad(e.target.value)}>
                                <option value="">Todas las actividades</option>
                                {actividades.map(a => <option key={a} value={a}>{a}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={4} xs={12} className={mobile ? 'mt-2' : ''}>
                            <button
                                className={`btn btn-sm w-100 ${filtroOperacion || filtroActividad
                                    ? 'btn-warning'
                                    : 'btn-outline-secondary'}`}
                                style={mobile ? { fontSize: 13, padding: '6px 0' } : {}}
                                onClick={() => {
                                    setFiltroOperacion('')
                                    setFiltroActividad('')
                                }}>
                                Limpiar filtros
                            </button>
                        </Col>
                    </Row>
                    <Row className={rowClass}>
                        <Col md={4} xs={colSize} className={cardClass}>
                            <Card body className="text-center" style={mobile ? { fontSize: 13, padding: 8 } : {}}>
                                <h6 style={mobile ? { fontSize: 12 } : {}}>Operaciones distintas</h6>
                                <h3 style={mobile ? { fontSize: 18 } : {}}>{totalOperaciones}</h3>
                            </Card>
                        </Col>
                        <Col md={4} xs={colSize} className={cardClass}>
                            <Card body className="text-center" style={mobile ? { fontSize: 13, padding: 8 } : {}}>
                                <h6 style={mobile ? { fontSize: 12 } : {}}>Operación más frecuente</h6>
                                <h3 style={mobile ? { fontSize: 18 } : {}}>{operacionMasFrecuente}</h3>
                            </Card>
                        </Col>
                        <Col md={4} xs={colSize} className={cardClass}>
                            <Card body className="text-center" style={mobile ? { fontSize: 13, padding: 8 } : {}}>
                                <h6 style={mobile ? { fontSize: 12 } : {}}>Promedio actividades/operación</h6>
                                <h3 style={mobile ? { fontSize: 18 } : {}}>{promedioActividades}</h3>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} xs={colSize} className={mobile ? 'mb-2' : ''}>
                            {chartActividadesPorOperacion.series &&
                                <div className="chart-shadow" style={mobile ? { padding: 4 } : {}}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartActividadesPorOperacion} />
                                </div>}
                        </Col>
                        <Col md={6} xs={colSize}>
                            {chartHorasPorOperacion.series &&
                                <div className="chart-shadow" style={mobile ? { padding: 4 } : {}}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartHorasPorOperacion} />
                                </div>}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} xs={colSize}>
                            {chartActividadesPorHora.series &&
                                <div className="chart-shadow" style={mobile ? { padding: 4 } : {}}>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={chartActividadesPorHora} />
                                </div>}
                        </Col>
                    </Row>
                </Tab>
            </Tabs>
        </div>
    );
};